import _ from "lodash";
/** External */
import update from "immutability-helper";
import client from "lib/apolloClient/client";

/** Internal */
import axios from "lib/axiosInstance";
import ACLStore from "lib/aclStore";
import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
import {
  addUserActivityMutation,
  manualSyncAnalyticsDataMutation,
} from "ToddleAnalytics/modules/ToddleAnalyticsMutations";
import { AI_INSIGHTS_HELP_CENTER_ARTICLE_URL } from "AIAnalyticsV2/modules/AIAnalyticsV2Constants";
import { getUserAgent } from "Utils/DeviceDetectHelpers";
import { getBackendServerUrl } from "Utils";
import {
  isFirstVisitToday,
  setUserVisitDataIntoStorage,
  getMomentDateWithSchoolTimeZone,
} from "ToddleAnalytics/modules/Utils";
import {
  downloadFromUrl,
  goToRelativeRoute,
  openLinkInNewTab,
} from "modules/Services";
import {
  checkManualSyncEligibility,
  getAnalyticsDataExportQuery,
} from "./ToddleAnalyticsQueries";

const getOrganizationId = state => {
  const userInfo = getUserInfo();
  return userInfo.org_id;
};

export const NAME = "toddleAnalytics";

export const UPDATE_GLOBAL_FILTER = "UPDATE_GLOBAL_FILTER " + NAME;
export const UPDATE_PERFORMANCE_FILTER = "UPDATE_PERFORMANCE_FILTER " + NAME;
export const UPDATE_PERFORMANCE_TABLE_FILTER =
  "UPDATE_PERFORMANCE_TABLE_FILTER " + NAME;
export const UPDATE_TABLE_FILTERS = "UPDATE_TABLE_FILTERS" + NAME;
export const UPDATE_ENGAGEMENT_FILTER = "UPDATE_ENGAGEMENT_FILTER" + NAME;
export const UPDATE_BREADCRUMB_STATE = "UPDATE_BREADCRUMB_STATE" + NAME;
export const RESET_TABLE_FILTERS = "RESET_TABLE_FILTERS" + NAME;
export const UPDATE_AI_INSIGHTS_FILTERS = "UPDATE_AI_INSIGHTS_FILTERS" + NAME;
export const UPDATE_AI_PLAYGROUND_FILTERS =
  "UPDATE_AI_PLAYGROUND_FILTERS" + NAME;
export const SET_ACTIVE_AI_CONVERSATION_ID =
  "SET_ACTIVE_AI_CONVERSATION_ID " + NAME;
export const SET_AI_CONVERSATION_STATE = "SET_AI_CONVERSATION_STATE " + NAME;
export const UPDATE_KEY_INSIGHTS = "UPDATE_KEY_INSIGHTS " + NAME;
export const UPDATE_TOKEN_EXHAUSTION_STATUS =
  "UPDATE_TOKEN_EXHAUSTION_STATUS " + NAME;
export const SET_DASHBOARD_EDIT_MODE = "SET_DASHBOARD_EDIT_MODE " + NAME;
export const SET_PENDING_NAVIGATION = "SET_PENDING_NAVIGATION " + NAME;
export const CLEAR_PENDING_NAVIGATION = "CLEAR_PENDING_NAVIGATION " + NAME;

export const getAnalyticsData = ({ path, queryVariables, setData }) => {
  return async dispatch => {
    try {
      const accessToken = _.get(JSON.parse(localStorage.userInfo), "token", "");
      const response = axios
        .post(
          getBackendServerUrl({
            path,
          }),
          queryVariables,
          {
            headers: { Authorization: `Bearer ${accessToken}` },
          }
        )
        .then(res => {
          setData({ data: _.get(res, "data", {}), networkStatus: 7 });
        });
      setData({ data: _.get(response, "data", {}), networkStatus: 1 });
    } catch (error) {
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
    }
  };
};

export const trackUserVisit = async () => {
  try {
    const isFirstVisit = isFirstVisitToday();
    if (!isFirstVisit || !ACLStore.can("Analytics:TrackUserActivity")) {
      return;
    }
    const input = {
      activityTimestamp: getMomentDateWithSchoolTimeZone(),
      source: "WEB",
      userAgent: getUserAgent(),
    };
    const res = await client.mutate({
      mutation: addUserActivityMutation,
      variables: {
        input,
      },
    });
    if (!_.isEmpty(_.get(res, "data", {}))) {
      const userInfo = getUserInfo({ portalType: "PLANNER" });
      setUserVisitDataIntoStorage({
        userId: userInfo.id,
        timeStamp: input.activityTimestamp,
      });
    }
  } catch (e) {}
};

export const updatePerformanceTableFilter = payload => ({
  type: UPDATE_PERFORMANCE_TABLE_FILTER,
  payload,
});

export const updateTableFilters = payload => ({
  type: UPDATE_TABLE_FILTERS,
  payload,
});

export const resetTableFilters = payload => {
  return {
    type: RESET_TABLE_FILTERS,
    payload,
  };
};

export const updateAIInsightsFilters = payload => {
  return {
    type: UPDATE_AI_INSIGHTS_FILTERS,
    payload,
  };
};

export const updateAIPlaygroundFilters = payload => {
  return {
    type: UPDATE_AI_PLAYGROUND_FILTERS,
    payload,
  };
};

export const setActiveAIConversationId = payload => {
  return {
    type: SET_ACTIVE_AI_CONVERSATION_ID,
    payload,
  };
};

export const updateBreadCrumbState = payload => ({
  type: UPDATE_BREADCRUMB_STATE,
  payload,
});

export const setAIConversationState = payload => ({
  type: SET_AI_CONVERSATION_STATE,
  payload,
});

export const updateKeyInsights = payload => ({
  type: UPDATE_KEY_INSIGHTS,
  payload,
});

export const updateTokenExhaustionStatus = payload => ({
  type: UPDATE_TOKEN_EXHAUSTION_STATUS,
  payload,
});

export const navigateToRoute = ({
  route,
  replacePath = null,
  type = "push",
  action = "preseveAll",
  queryParams = [],
}) => {
  return (dispatch, getState) => {
    const urlObj = new URL(window.location);
    const searchParams = urlObj.searchParams;

    // Convert searchParams to an array for safe iteration and deletion
    const searchParamsEntries = Array.from(searchParams.entries());

    switch (action) {
      case "preserveAll":
        // Do nothing, since the searchParams will be preserved by default
        break;
      case "removeAll":
        // Remove all query parameters
        searchParamsEntries.forEach(([key]) => {
          searchParams.delete(key);
        });
        break;
      case "preserveOnly":
        {
          // Remove all except specified query parameters
          const paramsToPreserve = new Set(queryParams);
          searchParamsEntries.forEach(([key]) => {
            if (!paramsToPreserve.has(key)) {
              searchParams.delete(key);
            }
          });
        }
        break;
      case "removeOnly":
        // Remove only specified query parameters
        queryParams.forEach(param => {
          searchParams.delete(param);
        });
        break;
      default:
        break;
    }

    const newQueryParamString = searchParams.toString();
    let newRoute = route;
    if (newQueryParamString) {
      if (_.includes(route, "?")) {
        newRoute = `${route}&${newQueryParamString}`;
      } else {
        newRoute = `${route}?${newQueryParamString}`;
      }
    }

    dispatch(
      goToRelativeRoute({
        route: newRoute,
        type,
        replacePath,
      })
    );
  };
};

export const setDashboardEditMode = payload => ({
  type: SET_DASHBOARD_EDIT_MODE,
  payload,
});

export const setPendingNavigation = payload => ({
  type: SET_PENDING_NAVIGATION,
  payload,
});

export const clearPendingNavigation = () => ({
  type: CLEAR_PENDING_NAVIGATION,
});

export const performPendingNavigation = () => (dispatch, getState) => {
  const state = getState();
  const pending = _.get(state, [NAME, "pendingNavigation"]);
  if (!pending) return;
  dispatch(clearPendingNavigation());
  // pending should be the arguments accepted by navigateToRoute
  dispatch(
    navigateToRoute({
      route: pending.route,
      replacePath: pending.replacePath,
      type: pending.type,
      action: pending.action,
      queryParams: pending.queryParams,
    })
  );
};

export const setSearchParams = newParams => {
  return dispatch => {
    const currentParams = new URLSearchParams(location.search);
    Object.keys(newParams).forEach(key => {
      if (_.isNull(newParams[key]) || _.isUndefined(newParams[key])) {
        currentParams.delete(key);
      } else {
        currentParams.set(key, newParams[key]);
      }
    });

    dispatch(
      goToRelativeRoute({
        route: `${location.pathname}?${currentParams.toString()}`,
        type: "replace",
      })
    );
  };
};

export const downloadAnalyticsData = ({
  exportMetricType = "INDIVIDUAL",
  individualMetricFilters,
  sheetName,
}) => {
  return async dispatch => {
    try {
      const organizationId = getOrganizationId();

      const data = await client.query({
        query: getAnalyticsDataExportQuery,
        variables: {
          id: organizationId,
          exportFilters: {
            exportMetricType,
            individualMetricFilters,
          },
        },
        fetchPolicy: "network-only",
      });

      const downloadData = _.get(
        data,
        "data.node.analytics.metricDataExport",
        {}
      );

      const downloadUrl = _.get(downloadData, "url");

      if (downloadUrl) {
        return dispatch(
          downloadFromUrl({
            attachment: {
              url: downloadUrl,
              name: "Insights",
              mimeType: "application/xlsx",
            },
            name: sheetName,
            shouldAskForSave: true,
          })
        );
      }
    } catch (error) {
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
    }
  };
};

export const manualSyncAnalyticsData = ({
  input = {},
  queryVariables = {},
}) => {
  return async dispatch => {
    try {
      await client.mutate({
        mutation: manualSyncAnalyticsDataMutation,
        variables: {
          input,
        },
        refetchQueries: [
          {
            query: checkManualSyncEligibility,
            variables: queryVariables,
          },
        ],
        awaitRefetchQueries: true,
      });

      dispatch(
        setToastMsg({
          msg: "toastMsgs:data_sync_has_started_this_may_take_a_few_moments",
          type: "success",
        })
      );
    } catch (e) {
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
    }
  };
};

const REDUCER_HANDLERS = {
  [UPDATE_GLOBAL_FILTER]: (state, action) => {
    const { params } = action.payload;
    _.forEach(params, (value, key) => {
      state = update(state, { globalFilter: { [key]: { $set: value } } });
    });
    return state;
  },
  [UPDATE_PERFORMANCE_FILTER]: (state, action) => {
    const { params, filterType } = action.payload;
    _.forEach(params, (value, key) => {
      const stateChanges = { performance: {} };
      const valueUpdate = { [key]: { $set: value } };
      if (filterType) {
        stateChanges.performance[filterType] = valueUpdate;
      } else {
        stateChanges.performance = valueUpdate;
      }
      state = update(state, stateChanges);
    });
    return state;
  },
  [UPDATE_PERFORMANCE_TABLE_FILTER]: (state, action) => {
    const { params, tableName } = action.payload;
    _.forEach(params, (value, key) => {
      const stateChanges = { tableFilters: {} };
      if (_.isEmpty(state.tableFilters[tableName])) {
        stateChanges.tableFilters = { [tableName]: { $set: { [key]: value } } };
      } else {
        const valueUpdate = { [key]: { $set: value } };
        stateChanges.tableFilters[tableName] = valueUpdate;
      }
      state = update(state, stateChanges);
    });
    return state;
  },
  [UPDATE_ENGAGEMENT_FILTER]: (state, action) => {
    const { params, filterType } = action.payload;
    _.forEach(params, (value, key) => {
      const stateChanges = { engagement: {} };
      const valueUpdate = { [key]: { $set: value } };
      stateChanges.engagement[filterType] = valueUpdate;
      state = update(state, stateChanges);
    });
    return state;
  },
  [UPDATE_TABLE_FILTERS]: (state, action) => {
    const { params, tableName } = action.payload;
    _.forEach(params, (value, key) => {
      const stateChanges = { tableFilters: {} };
      if (_.isEmpty(state.tableFilters[tableName])) {
        stateChanges.tableFilters = { [tableName]: { $set: { [key]: value } } };
      } else {
        const valueUpdate = { [key]: { $set: value } };
        stateChanges.tableFilters[tableName] = valueUpdate;
      }
      state = update(state, stateChanges);
    });
    return state;
  },
  [UPDATE_BREADCRUMB_STATE]: (state, action) => {
    const { hierarchy } = action.payload;
    state = update(state, { breadCrumbState: { $set: hierarchy } });
    return state;
  },
  [RESET_TABLE_FILTERS]: (state, action) => {
    state = update(state, { tableFilters: { $set: {} } });
    return state;
  },
  [UPDATE_AI_INSIGHTS_FILTERS]: (state, action) => {
    return update(state, {
      aiInsightsFilters: {
        curriculumProgramId: {
          $set: _.get(action, "payload.curriculumProgramId"),
        },
      },
    });
  },
  [SET_ACTIVE_AI_CONVERSATION_ID]: (state, action) => {
    return update(state, {
      activeAIConversationId: { $set: _.get(action, "payload", null) },
    });
  },
  [UPDATE_AI_PLAYGROUND_FILTERS]: (state, action) => {
    return update(state, {
      aiPlaygroundFilters: {
        curriculumProgramId: {
          $set: _.get(action, "payload.curriculumProgramId"),
        },
      },
    });
  },
  [SET_AI_CONVERSATION_STATE]: (state, action) => {
    const {
      aiConversationId,
      currentAIState,
      progressIndicatorValue,
      allowProgressReset = false,
    } = action.payload || {};

    // Build updates object, keeping explicit values (including empty string)
    const updates = {};
    if (!_.isNil(currentAIState)) {
      updates.currentAIState = currentAIState;
    }
    if (!_.isNil(progressIndicatorValue)) {
      updates.progressIndicatorValue = progressIndicatorValue;
    }

    // If no updates, return state unchanged
    if (_.isEmpty(updates)) {
      return state;
    }

    const existingConversation = _.get(state, [
      "aiConversationState",
      aiConversationId,
    ]);

    // Prevent decreasing progress unless explicitly allowed (for resets)
    if (
      !allowProgressReset &&
      existingConversation &&
      _.isNumber(updates.progressIndicatorValue) &&
      _.isNumber(existingConversation.progressIndicatorValue) &&
      updates.progressIndicatorValue <
        existingConversation.progressIndicatorValue
    ) {
      delete updates.progressIndicatorValue;
    }

    return update(state, {
      aiConversationState: {
        [aiConversationId]: existingConversation
          ? { $merge: updates }
          : { $set: updates },
      },
    });
  },
  [UPDATE_KEY_INSIGHTS]: (state, action) => {
    const { chartId, keyInsights, isStreaming } = action.payload || {};

    if (!chartId) {
      return state;
    }

    const current = state.keyInsights?.[chartId] || {};

    const merged = {
      ...current,
      keyInsights,
      ...(!_.isNil(isStreaming) && { isStreaming }),
    };

    return update(state, {
      keyInsights: {
        [chartId]: { $set: merged },
      },
    });
  },
  [UPDATE_TOKEN_EXHAUSTION_STATUS]: (state, action) => {
    const { aiConversationId, tokenLimit, tokenConsumed } =
      action.payload || {};

    if (!aiConversationId) {
      return state;
    }

    const existingStatus = state.tokenExhaustionStatus?.[aiConversationId];

    const updates = {
      ...(tokenLimit && { tokenLimit }),
      ...(tokenConsumed && { tokenConsumed }),
    };

    return update(state, {
      tokenExhaustionStatus: {
        [aiConversationId]: existingStatus
          ? { $merge: updates }
          : { $set: updates },
      },
    });
  },
  [SET_DASHBOARD_EDIT_MODE]: (state, action) => {
    return update(state, { dashboardEditMode: { $set: !!action.payload } });
  },
  [SET_PENDING_NAVIGATION]: (state, action) => {
    return update(state, { pendingNavigation: { $set: action.payload } });
  },
  [CLEAR_PENDING_NAVIGATION]: (state, action) => {
    return update(state, { pendingNavigation: { $set: null } });
  },
};

const initialState = {
  globalFilter: {},
  tableFilters: {},
  breadCrumbState: [],
  aiInsightsFilters: { curriculumProgramId: null },
  aiPlaygroundFilters: { curriculumProgramId: null },
  activeAIConversationId: null,
  aiConversationState: {},
  keyInsights: {},
  tokenExhaustionStatus: {},
  dashboardEditMode: false,
  pendingNavigation: null,
};

export default function myReducer(state = initialState, action) {
  const handler = REDUCER_HANDLERS[action.type];
  return handler ? handler(state, action) : state;
}

export const goToHelpCentreArticle = () => {
  return dispatch => {
    dispatch(openLinkInNewTab({ url: AI_INSIGHTS_HELP_CENTER_ARTICLE_URL }));
  };
};
